import { useMemo, useState } from "react";
import { categories, newsFeed, type Category } from "../../../mocks/news";

const tagStyle: Record<Category, string> = {
  娱乐: "bg-primary-100 text-primary-700",
  社会: "bg-secondary-100 text-secondary-700",
  科技: "bg-accent-100 text-accent-800",
  民生: "bg-accent-100 text-accent-700",
  文体: "bg-primary-50 text-primary-700",
};

export default function NewsFeed() {
  const [cat, setCat] = useState<Category | "全部">("全部");
  const [kw, setKw] = useState("");

  const filtered = useMemo(() => {
    return newsFeed.filter((n) => {
      const okCat = cat === "全部" || n.category === cat;
      const okKw = !kw || n.title.includes(kw) || n.excerpt.includes(kw);
      return okCat && okKw;
    });
  }, [cat, kw]);

  return (
    <section id="latest" className="scroll-mt-20 bg-background-100/60 py-14 md:py-20">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="flex items-center gap-2 text-sm font-semibold text-primary-600">
              <span className="h-px w-6 bg-primary-400" aria-hidden="true" />
              最新爆料
            </p>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-4xl">
              新瓜速递
            </h2>
            <p className="mt-3 max-w-2xl text-sm text-foreground-600">
              刚刚出炉的劲爆消息按时间倒序滚动更新，可切换分类或用关键词精准搜索。
            </p>
          </div>
          <div className="flex w-full max-w-sm items-center gap-2 rounded-full bg-background-50 px-4 py-2 ring-1 ring-background-300 focus-within:ring-primary-400">
            <i className="ri-search-line text-foreground-500" aria-hidden="true" />
            <input
              value={kw}
              onChange={(e) => setKw(e.target.value)}
              type="search"
              placeholder="搜索瓜点关键词"
              className="w-full bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
              aria-label="搜索瓜点关键词"
            />
            {kw && (
              <button type="button" onClick={() => setKw("")} aria-label="清空搜索">
                <i className="ri-close-circle-fill text-foreground-400" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          {(["全部", ...categories] as (Category | "全部")[]).map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCat(c)}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors whitespace-nowrap ${
                cat === c
                  ? "bg-primary-500 text-white"
                  : "bg-background-50 text-foreground-600 ring-1 ring-background-200 hover:text-foreground-900"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="mt-10 flex flex-col items-center rounded-2xl border border-dashed border-background-300 bg-background-50 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-2xl text-foreground-400">
              <i className="ri-search-line" aria-hidden="true" />
            </span>
            <p className="mt-4 font-heading text-lg font-bold text-foreground-800">没有找到相关新瓜</p>
            <p className="mt-1 text-sm text-foreground-500">换个关键词或分类试试吧</p>
          </div>
        ) : (
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((n) => (
              <article
                key={n.id}
                className="group flex flex-col overflow-hidden rounded-2xl border border-background-200 bg-background-50 shadow-card transition-transform hover:-translate-y-1"
              >
                <div className="relative aspect-[3/2] overflow-hidden bg-background-200">
                  <img
                    src={n.image}
                    alt={n.title}
                    title={`${n.title} - 吃瓜最新当天热点`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 rounded-full bg-background-50/90 px-2.5 py-1 text-xs font-medium text-foreground-700 backdrop-blur-sm">
                    {n.category}
                  </span>
                  <span className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-primary-500/90 px-2 py-1 text-[11px] font-medium text-white">
                    <i className="ri-fire-fill" aria-hidden="true" />
                    热度 {n.hot}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <h3 className="font-heading text-base font-bold leading-snug text-foreground-900 transition-colors group-hover:text-primary-700 md:text-lg">
                    <a href="#latest">{n.title}</a>
                  </h3>
                  <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-600">
                    {n.excerpt}
                  </p>
                  <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3 text-xs text-foreground-500">
                    <span className={`rounded-full px-2.5 py-0.5 ${tagStyle[n.category]}`}>{n.category}</span>
                    <span className="flex items-center gap-1.5">
                      <i className="ri-time-line" aria-hidden="true" />
                      {n.time}
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}